// src/pages/DebtStudio/components/Issues/DealStructure/StructureGrid.jsx

import React, { useMemo, useCallback, useState, useEffect } from "react";
import ExcelGrid from "components/ExcelGrid";
import dayjs from "lib/dayjs";
import { Plus, Trash2 } from "lucide-react";
import { fmtTrunc3 } from "pages/DebtStudio/utils/formatters";

// ────────────────────────────────
// Normalizers
// ────────────────────────────────
const normNum = (v) => {
  if (v === "" || v == null) return null;
  const n = Number(v);
  return Number.isNaN(n) ? null : n;
};

const normPct = (v) => {
  if (v === "" || v == null) return null;
  const s = String(v).trim().replace(/%/g, "");
  const n = Number(s);
  return Number.isNaN(n) ? null : n; // literal percent, no scaling
};

const normDate = (v) => {
  if (!v) return null;
  const s = dayjs(v);
  return s.isValid() ? s.format("YYYY-MM-DD") : null;
};

const same = (a, b) => (Number.isNaN(a) && Number.isNaN(b)) || a === b;

function ensureRowKey(row, idx) {
  return (
    row?._gridKey ||
    `row-${idx}-${Math.random().toString(36).slice(2, 9)}`
  );
}

function getUniqueTermGroupName(baseName, rows, excludeIdxs = []) {
  const used = new Set(
    (Array.isArray(rows) ? rows : [])
      .filter((_, idx) => !excludeIdxs.includes(idx))
      .map((r) => String(r?.term_group || "").trim())
      .filter(Boolean)
  );

  if (!used.has(baseName)) return baseName;

  let i = 2;
  while (used.has(`${baseName}-${i}`)) i += 1;

  return `${baseName}-${i}`;
}

function normalizeTermGroups(nextRows) {
  if (!Array.isArray(nextRows)) return [];

  const groups = new Map();

  for (const row of nextRows) {
    if ((row?.bond_type || "serial") === "term" && row?.term_group) {
      if (!groups.has(row.term_group)) groups.set(row.term_group, []);
      groups.get(row.term_group).push(row);
    }
  }

  return nextRows.map((row) => {
    if ((row?.bond_type || "serial") !== "term" || !row?.term_group) {
      return row;
    }

    const members = groups.get(row.term_group) || [];
    if (members.length === 0) {
      return { ...row, bond_type: "serial", term_group: null };
    }

    return row;
  });
}

function getTermGroupRowIndexes(rows, termGroup) {
  if (!termGroup) return [];
  return (Array.isArray(rows) ? rows : [])
    .map((row, idx) => ({ row, idx }))
    .filter(({ row }) => row?.term_group === termGroup)
    .map(({ idx }) => idx);
}

export default function StructureGrid({
  isLocked = false,
  financingType = "bond",

  rows,
  onBulkRowsChange = null,
  setRows = null,
}) {
  const [checked, setChecked] = useState([]);

  const isLoan =
    String(financingType || "").toLowerCase() === "loan";

  // When locking flips on, clear any selection state immediately.
  useEffect(() => {
    if (isLocked && checked.length) setChecked([]);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isLocked]);

  const pushRowsUpstream = useCallback(
    (nextRows) => {
      const normalized = normalizeTermGroups(nextRows);
      if (typeof onBulkRowsChange === "function") onBulkRowsChange(normalized);
      else if (typeof setRows === "function") setRows(normalized);
    },
    [onBulkRowsChange, setRows]
  );

  /* ────────────────────────────────
     Group + checkbox prep
  ──────────────────────────────── */
  const existingTermGroups = useMemo(() => {
    return Array.from(
      new Set((rows || []).map((r) => r.term_group).filter(Boolean))
    ).sort((a, b) => {
      const ay = parseInt(String(a).replace(/\D/g, ""), 10);
      const by = parseInt(String(b).replace(/\D/g, ""), 10);
      if (Number.isNaN(ay) || Number.isNaN(by))
        return String(a).localeCompare(String(b));
      return ay - by;
    });
  }, [rows]);

  const gridRows = useMemo(() => {
    const lastByGroup = {};
    for (const r of rows || []) {
      if (r.term_group) {
        if (
          !lastByGroup[r.term_group] ||
          (r.date && r.date > lastByGroup[r.term_group].date)
        ) {
          lastByGroup[r.term_group] = r;
        }
      }
    }

    return (rows || []).map((r, idx) => ({
      ...r,
      _gridKey: ensureRowKey(r, idx),
      bond_type: r.bond_type || "serial",
      term_group_options: existingTermGroups,
      _checked: !isLocked && checked.includes(idx),
      _isLastInGroup:
        r.term_group && lastByGroup[r.term_group]?.date === r.date,
    }));
  }, [rows, existingTermGroups, checked, isLocked]);

  /* ────────────────────────────────
     Checkbox + term group handlers
  ──────────────────────────────── */
  const handleCheckRow = (rowIdx, checkedVal) => {
    if (isLocked) return;

    setChecked((prev) =>
      checkedVal
        ? [...prev, rowIdx].filter((v, i, arr) => arr.indexOf(v) === i)
        : prev.filter((v) => v !== rowIdx)
    );
  };

  const handleAssignTermGroup = () => {
    if (isLocked) return;
    if (!checked.length) return;

    const eligibleIdxs = checked.filter(
      (i) => rows[i] && (rows[i].bond_type || "serial") === "serial"
    );
    if (!eligibleIdxs.length) return;

    const lastRow = eligibleIdxs
      .map((i) => rows[i])
      .reduce(
        (latest, r) =>
          !latest || (r.date && r.date > latest.date) ? r : latest,
        null
      );
    if (!lastRow?.date) return;

    const yyyy = lastRow.date.slice(0, 4);
    const baseGroupName = `${yyyy} Term`;
    const groupName = getUniqueTermGroupName(
      baseGroupName,
      rows,
      eligibleIdxs
    );

    const updated = rows.map((row, idx) =>
      eligibleIdxs.includes(idx)
        ? {
            ...row,
            bond_type: "term",
            term_group: groupName,
          }
        : row
    );

    pushRowsUpstream(updated);
    setChecked([]);
  };

  const handleUnassignTermGroup = (rowIdx) => {
    if (isLocked) return;

    const updated = rows.map((row, idx) =>
      idx === rowIdx ? { ...row, bond_type: "serial", term_group: null } : row
    );

    pushRowsUpstream(updated);
  };

  /* ────────────────────────────────
     Row add / delete handlers
  ──────────────────────────────── */
  const handleAddBelow = useCallback(
    (rowIdx) => {
      if (isLocked) return;

      const baseRows = Array.isArray(rows) ? rows : [];
      const sourceRow = baseRows[rowIdx];
      if (!sourceRow) return;

      const isTermAnchor =
        (sourceRow.bond_type || "serial") === "term" &&
        !!sourceRow.term_group &&
        gridRows[rowIdx]?._isLastInGroup;

      if (isTermAnchor) {
        const memberIndexes = getTermGroupRowIndexes(baseRows, sourceRow.term_group);
        if (!memberIndexes.length) return;

        const baseGroupName = `${String(sourceRow.date || "").slice(0, 4)} Term`;
        const newGroupName = getUniqueTermGroupName(baseGroupName, baseRows);

        const clonedGroupRows = memberIndexes.map((idx, cloneIdx) => {
          const row = baseRows[idx];
          return {
            ...row,
            term_group: newGroupName,
            _gridKey: `row-${Date.now()}-${cloneIdx}-${Math.random()
              .toString(36)
              .slice(2, 9)}`,
          };
        });

        const updated = [...baseRows];
        updated.splice(rowIdx + 1, 0, ...clonedGroupRows);

        pushRowsUpstream(updated);

        setChecked((prev) =>
          prev.map((idx) => (idx > rowIdx ? idx + clonedGroupRows.length : idx))
        );
        return;
      }

      const newRow = {
        ...sourceRow,
        _gridKey: `row-${Date.now()}-${Math.random()
          .toString(36)
          .slice(2, 9)}`,
      };

      const updated = [...baseRows];
      updated.splice(rowIdx + 1, 0, newRow);

      pushRowsUpstream(updated);

      setChecked((prev) =>
        prev.map((idx) => (idx > rowIdx ? idx + 1 : idx))
      );
    },
    [isLocked, rows, gridRows, pushRowsUpstream]
  );

  const handleDeleteRow = useCallback(
    (rowIdx) => {
      if (isLocked) return;

      const baseRows = Array.isArray(rows) ? rows : [];
      const sourceRow = baseRows[rowIdx];
      if (!sourceRow) return;

      const isTermAnchor =
        (sourceRow.bond_type || "serial") === "term" &&
        !!sourceRow.term_group &&
        gridRows[rowIdx]?._isLastInGroup;

      if (isTermAnchor) {
        const memberIndexes = new Set(
          getTermGroupRowIndexes(baseRows, sourceRow.term_group)
        );

        if (memberIndexes.size === 0) return;

        if (baseRows.length <= memberIndexes.size) {
          void alert("At least one structure row must remain.", {
            title: "Cannot delete row",
            okLabel: "OK",
          });
          return;
        }

        const updated = baseRows.filter((_, idx) => !memberIndexes.has(idx));
        pushRowsUpstream(updated);

        setChecked((prev) =>
          prev
            .filter((idx) => !memberIndexes.has(idx))
            .map((idx) => {
              const removedBefore = [...memberIndexes].filter(
                (removedIdx) => removedIdx < idx
              ).length;
              return idx - removedBefore;
            })
        );
        return;
      }

      if (baseRows.length <= 1) {
        void alert("At least one structure row must remain.", {
          title: "Cannot delete row",
          okLabel: "OK",
        });
        return;
      }

      const updated = baseRows.filter((_, idx) => idx !== rowIdx);
      pushRowsUpstream(updated);

      setChecked((prev) =>
        prev
          .filter((idx) => idx !== rowIdx)
          .map((idx) => (idx > rowIdx ? idx - 1 : idx))
      );
    },
    [isLocked, rows, gridRows, pushRowsUpstream, alert]
  );

  /* ────────────────────────────────
     Grid edit handler
  ──────────────────────────────── */
  const handleGridChange = useCallback(
    (newRows) => {
      if (isLocked) return;

      const baseRows = Array.isArray(rows) ? rows : [];
      if (!Array.isArray(newRows) || !baseRows.length) return;

      let changed = false;

      const updated = newRows.map((r, rowIdx) => {
        const old = baseRows[rowIdx];
        if (!old) return r;

        const nextDate = normDate(r.date) ?? normDate(old.date);
        const nextCallDate = normDate(r.call_date);
        const nextCallPrice = isLoan ? null : (normNum(r.call_price) ?? 100);
        const nextPrincipal = normNum(r.principal);
        const nextCoupon = normPct(r.coupon);

        // For loans, coupon is displayed as Interest Rate and yield is hidden.
        // Keep yield equal to coupon so loan price remains par.
        const nextYield = isLoan ? nextCoupon : normPct(r.yield);

        const nextBondType = r.bond_type ?? old.bond_type ?? "serial";

        let nextTermGroup = old.term_group ?? null;
        if (nextBondType !== "term") {
          nextTermGroup = null;
        } else if (!nextTermGroup && nextDate) {
          const baseGroupName = `${String(nextDate).slice(0, 4)} Term`;
          nextTermGroup = getUniqueTermGroupName(baseGroupName, baseRows, [
            rowIdx,
          ]);
        }

        const nextRow = {
          ...old,
          ...r,
          _gridKey: old._gridKey || r._gridKey || ensureRowKey(old, rowIdx),
          date: nextDate,
          call_date: nextCallDate,
          call_price: nextCallPrice,
          principal: nextPrincipal,
          coupon: nextCoupon,
          yield: nextYield,
          bond_type: nextBondType,
          term_group: nextTermGroup,
          price: isLoan ? 100 : old.price,
        };

        const materiallyChanged =
          nextRow.date !== normDate(old.date) ||
          nextRow.call_date !== normDate(old.call_date) ||
          !same(nextRow.call_price, normNum(old.call_price) ?? 100) ||
          !same(nextRow.principal, normNum(old.principal)) ||
          !same(nextRow.coupon, normPct(old.coupon)) ||
          !same(nextRow.yield, normPct(old.yield)) ||
          (nextRow.bond_type ?? "serial") !== (old.bond_type ?? "serial") ||
          (nextRow.term_group ?? null) !== (old.term_group ?? null) ||
          !same(nextRow.price, normNum(old.price));

        if (materiallyChanged) changed = true;

        return nextRow;
      });

      if (!changed) return;

      pushRowsUpstream(updated);
    },
    [isLocked, isLoan, rows, pushRowsUpstream]
  );

  /* ────────────────────────────────
     Columns
  ──────────────────────────────── */
  const columns = useMemo(() => {
    const editableWhenUnlocked = (editable) => (!isLocked ? !!editable : false);

    const baseColumns = [
      {
        key: "date",
        label: "Date",
        editable: editableWhenUnlocked(true),
        date: true,
        editor: "date",
        type: "date",
        width: "w-28",
        className: "numeric",
      },

      !isLoan && {
        key: "call_date",
        label: "Call Date",
        editable: editableWhenUnlocked(true),
        date: true,
        editor: "date",
        type: "date",
        width: "w-28",
        className: "numeric",
      },

      !isLoan && {
        key: "call_price",
        label: "Call Price",
        editable: editableWhenUnlocked(true),
        numeric: true,
        width: "w-24",
        className: "text-right numeric",
        headerClassName: "text-right",
        format: (v) => v != null && v !== "" ? Number(v).toFixed(3) : "100.000",
        copyValue: (row) => String(row.call_price ?? 100),
      },

      {
        key: "principal",
        label: "Principal",
        editable: editableWhenUnlocked(true),
        numeric: true,
        width: "w-28",
        className: "text-right numeric",
        headerClassName: "text-right",
        format: (v) =>
          v != null
            ? Number(v).toLocaleString(undefined, {
                minimumFractionDigits: 2,
                maximumFractionDigits: 2,
              })
            : "",
        copyValue: (row) =>
          row.principal != null && row.principal !== ""
            ? String(row.principal)
            : "",
      },

      {
        key: "coupon",
        label: isLoan ? "Interest Rate (%)" : "Coupon (%)",
        editable: editableWhenUnlocked(true),
        numeric: true,
        width: "w-24",
        className: "text-right numeric",
        headerClassName: "text-right",
        format: (v, row) =>
          (row.bond_type || "serial") === "term" && !row._isLastInGroup
            ? ""
            : v != null && v !== ""
            ? Number(v).toFixed(3)
            : "",
        copyValue: (row) => {
          if ((row.bond_type || "serial") === "term" && !row._isLastInGroup)
            return "";
          if (row.coupon == null || row.coupon === "") return "";
          return String(row.coupon);
        },
      },

      !isLoan && {
        key: "yield",
        label: "Yield (%)",
        editable: editableWhenUnlocked(true),
        numeric: true,
        width: "w-24",
        className: "text-right numeric",
        headerClassName: "text-right",
        format: (v, row) =>
          (row.bond_type || "serial") === "term" && !row._isLastInGroup
            ? ""
            : v != null && v !== ""
            ? Number(v).toFixed(3)
            : "",
        copyValue: (row) => {
          if ((row.bond_type || "serial") === "term" && !row._isLastInGroup)
            return "";
          if (row.yield == null || row.yield === "") return "";
          return String(row.yield);
        },
      },

      !isLoan && {
        key: "price",
        label: "Price",
        editable: false,
        numeric: true,
        width: "w-24",
        className: "text-right numeric",
        headerClassName: "text-right",
        format: (v, row) =>
          (row.bond_type || "serial") === "term" && !row._isLastInGroup
            ? ""
            : fmtTrunc3(v),
        copyValue: (row) => {
          if ((row.bond_type || "serial") === "term" && !row._isLastInGroup)
            return "";
          if (row.price == null || row.price === "") return "";
          return String(row.price);
        },
      },

      !isLoan && {
        key: "bond_type",
        label: "Bond Type",
        editable: false,
        width: "w-28",
        render: (row) => row.bond_type || "serial",
        copyValue: (row) => String(row.bond_type || "serial"),
      },

      !isLoan && {
        key: "term_group",
        label: (
          <div className="flex items-center gap-2">
            <span>Term Group</span>

            {!isLocked && checked.length > 0 && (
              <button
                type="button"
                className="linkCls text-xs font-normal"
                onMouseDown={(e) => e.stopPropagation()}
                onClick={(e) => {
                  e.stopPropagation();
                  handleAssignTermGroup();
                }}
                tabIndex={-1}
              >
                Assign
              </button>
            )}
          </div>
        ),
        editable: false,
        width: "w-32",
        render: (row, allRows, rowIdx) => {
          const stop = (e) => e.stopPropagation();

          return (row.bond_type || "serial") === "term" && row.term_group ? (
            <div className="flex items-center gap-1 min-w-0">
              {!isLocked && (
                <button
                  type="button"
                  title="Unassign Term"
                  className="text-xs px-1 py-0.5 rounded bg-border hover:bg-brand/20 text-muted-foreground"
                  onMouseDown={stop}
                  onClick={(e) => {
                    stop(e);
                    handleUnassignTermGroup(rowIdx);
                  }}
                  tabIndex={-1}
                >
                  ❌
                </button>
              )}

              <div className="flex-1 min-w-0 truncate">{row.term_group}</div>
            </div>
          ) : (
            <div className="flex items-center gap-1 min-w-0">
              {(row.bond_type || "serial") === "serial" ? (
                <input
                  type="checkbox"
                  disabled={isLocked}
                  checked={!!row._checked}
                  onMouseDown={stop}
                  onChange={(e) => {
                    stop(e);
                    handleCheckRow(rowIdx, e.target.checked);
                  }}
                  className={
                    isLocked ? "cursor-not-allowed opacity-50" : "cursor-pointer"
                  }
                />
              ) : (
                <div className="w-4" />
              )}

              <div className="flex-1 min-w-0 truncate" />
            </div>
          );
        },
        copyValue: (row) => (row.term_group ? String(row.term_group) : ""),
      },

      {
        key: "actions",
        label: "",
        editable: false,
        width: "w-16",
        render: (row, allRows, rowIdx) => {
          const isTermRow =
            (row.bond_type || "serial") === "term" && !!row.term_group;
          const isTermAnchor = isTermRow && !!row._isLastInGroup;

          if (isLocked) return null;
          if (isTermRow && !isTermAnchor) return null;

          return (
            <div className="flex items-center h-6 justify-start gap-1 pl-1">
              <button
                type="button"
                onMouseDown={(e) => e.stopPropagation()}
                onClick={(e) => {
                  e.stopPropagation();
                  handleAddBelow(rowIdx);
                }}
                title={
                  isTermAnchor ? "Clone term bond group below" : "Add row below"
                }
                className="text-green-600 hover:text-green-800"
                tabIndex={-1}
              >
                <Plus size={16} />
              </button>
              <button
                type="button"
                onMouseDown={(e) => e.stopPropagation()}
                onClick={(e) => {
                  e.stopPropagation();
                  handleDeleteRow(rowIdx);
                }}
                title={
                  isTermAnchor ? "Delete term bond group" : "Delete row"
                }
                className="text-red-500 hover:text-red-700"
                tabIndex={-1}
              >
                <Trash2 size={16} />
              </button>
            </div>
          );
        },
        copyValue: () => "",
      },
    ];

    return baseColumns.filter(Boolean);
  }, [
    isLocked,
    isLoan,
    checked,
    handleUnassignTermGroup,
    handleAddBelow,
    handleDeleteRow,
  ]);

  /* ────────────────────────────────
     Render
  ──────────────────────────────── */
  return (
    <div className="overflow-x-auto pb-2">
      <ExcelGrid
        columns={columns}
        rows={gridRows}
        onChange={isLocked ? undefined : handleGridChange}
      />
    </div>
  );
}
